import { useEffect, useState, useMemo, useCallback, FC } from "react"
import { Accordion, Card, Container, Row, Col, Form, Spinner } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js"
import { Bar } from "react-chartjs-2"
import API from "../common/API"
import { parsedUser } from "../common/GetCurrentUser"
import { FormatMoney, isSuperAdmin } from "../common/helpers"

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

interface SalesData {
    year: number
    month: number
    amount: number
}

interface ProcessedData {
    labels: string[]
    datasets: {
        label: string
        data: number[]
        backgroundColor: string
        borderColor: string
        borderWidth: number
        borderRadius: number
    }[]
}

interface OrgOption {
    id: string
    name: string
}

const COLORS = [
    { bg: "rgba(59, 130, 246, 0.7)",  border: "rgba(59, 130, 246, 1)"  },
    { bg: "rgba(16, 185, 129, 0.7)",  border: "rgba(16, 185, 129, 1)"  },
    { bg: "rgba(245, 158, 11, 0.7)",  border: "rgba(245, 158, 11, 1)"  },
    { bg: "rgba(239, 68, 68, 0.7)",   border: "rgba(239, 68, 68, 1)"   },
    { bg: "rgba(168, 85, 247, 0.7)",  border: "rgba(168, 85, 247, 1)"  },
]

const SalesByMonthPage: FC = () => {
    const { t } = useTranslation("global")
    const user = parsedUser()
    const canChooseOrg = isSuperAdmin(user)

    const [selectedOrgId, setSelectedOrgId] = useState<string>(
        canChooseOrg ? "" : ((user?.OrganizationId as string) ?? "")
    )
    const [orgs, setOrgs] = useState<OrgOption[]>([])
    const [isLoadingOrgs, setIsLoadingOrgs] = useState(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [salesData, setSalesData] = useState<SalesData[]>([])
    const [error, setError] = useState<string | null>(null)

    const loadOrgs = useCallback(async () => {
        if (!canChooseOrg) return
        try {
            setIsLoadingOrgs(true)
            const res = await API.getAction("Organization/AdminList", "Page=0&Quantity=200")
            if (res.data.status === "ok") {
                setOrgs(res.data.response.map((o: any) => ({ id: o.id, name: o.name })))
            }
        } catch (ex) {
            console.error(ex)
        } finally {
            setIsLoadingOrgs(false)
        }
    }, [canChooseOrg])

    useEffect(() => { loadOrgs() }, [loadOrgs])

    useEffect(() => {
        if (!selectedOrgId) {
            setSalesData([])
            return
        }
        const fetch = async () => {
            try {
                setLoading(true)
                setError(null)
                const res = await API.getAction(
                    `dashboard/SalesByMonthGeneral?OrganizationId=${selectedOrgId}`
                )
                if (res?.data?.response && Array.isArray(res.data.response)) {
                    setSalesData(res.data.response)
                } else {
                    setSalesData([])
                }
            } catch (err) {
                setError((err as Error)?.message || t("Error"))
                setSalesData([])
            } finally {
                setLoading(false)
            }
        }
        fetch()
    }, [selectedOrgId])

    const monthNames = useMemo(() => [
        t("Jan"), t("Feb"), t("Mar"), t("Apr"), t("May"), t("Jun"),
        t("Jul"), t("Aug"), t("Sep"), t("Oct"), t("Nov"), t("Dec"),
    ], [t])

    const chartData: ProcessedData | null = useMemo(() => {
        if (!salesData.length) return null

        const validData = salesData.filter(
            d => d.year != null && d.month != null && d.month >= 1 && d.month <= 12
        )
        if (!validData.length) return null

        const years = Array.from(new Set(validData.map(d => d.year))).sort()
        const allMonths = Array.from(new Set(validData.map(d => d.month))).sort((a, b) => a - b)

        const yearDataMap = new Map<number, Map<number, number>>()
        years.forEach(y => yearDataMap.set(y, new Map()))
        validData.forEach(item => yearDataMap.get(item.year)?.set(item.month, item.amount || 0))

        const datasets = years
            .map((year, i) => {
                const yearMap = yearDataMap.get(year)
                if (!yearMap) return null
                const color = COLORS[i % COLORS.length]
                return {
                    label: String(year),
                    data: allMonths.map(m => yearMap.get(m) || 0),
                    backgroundColor: color.bg,
                    borderColor: color.border,
                    borderWidth: 2,
                    borderRadius: 6,
                }
            })
            .filter((d): d is NonNullable<typeof d> => d !== null)

        return {
            labels: allMonths.map(m => (m >= 1 && m <= 12 ? monthNames[m - 1] : "")),
            datasets,
        }
    }, [salesData, monthNames])

    const totals = useMemo(() => {
        const result: Record<number, number> = {}
        salesData
            .filter(d => d.year != null && d.amount != null)
            .forEach(d => { result[d.year] = (result[d.year] || 0) + (d.amount || 0) })
        return result
    }, [salesData])

    const grandTotal = useMemo(
        () => salesData.reduce((sum, d) => sum + (d.amount || 0), 0),
        [salesData]
    )

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "top" as const,
                labels: { font: { size: 14, weight: "bold" as const }, padding: 20 },
            },
            title: {
                display: true,
                text: t("Sales Comparison by Month (Year over Year)"),
                font: { size: 20, weight: "bold" as const },
                padding: { bottom: 30 },
            },
            tooltip: {
                callbacks: {
                    label: (ctx: { dataset: { label?: string }; parsed: { y: number | null } }) => {
                        let label = ctx.dataset.label ? ctx.dataset.label + ": " : ""
                        if (ctx.parsed.y !== null) label += FormatMoney(ctx.parsed.y, 2)
                        return label
                    },
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { font: { size: 12, weight: "bold" as const } },
            },
            y: {
                beginAtZero: true,
                grid: { color: "rgba(0, 0, 0, 0.05)" },
                ticks: {
                    font: { size: 12 },
                    callback: (value: string | number) => FormatMoney(Number(value), 0),
                },
            },
        },
    }

    const sortedTableData = useMemo(() =>
        salesData
            .filter(d => d.year != null && d.month != null && d.month >= 1 && d.month <= 12 && d.amount != null)
            .sort((a, b) => a.year !== b.year ? b.year - a.year : a.month - b.month),
        [salesData]
    )

    return (
        <Container fluid className="px-4">
            <Row className="my-4 align-items-center">
                <Col>
                    <h2 className="mb-0">
                        {t("Sales By Month")} - {t("Year Comparison")}
                    </h2>
                </Col>
                {canChooseOrg && (
                    <Col md="auto">
                        <Form.Select
                            size="sm"
                            value={selectedOrgId}
                            onChange={e => setSelectedOrgId(e.target.value)}
                            disabled={isLoadingOrgs}
                            style={{ minWidth: 220 }}
                        >
                            <option value="">{t("Select Organization")}</option>
                            {orgs.map(o => (
                                <option key={o.id} value={o.id}>{o.name}</option>
                            ))}
                        </Form.Select>
                    </Col>
                )}
            </Row>

            {!selectedOrgId ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center text-muted py-5">
                        {t("Select an organization to load indicators")}
                    </Card.Body>
                </Card>
            ) : (
                <>
                    {/* Summary Cards */}
                    <Row className="mb-4">
                        {Object.entries(totals)
                            .sort(([a], [b]) => parseInt(b) - parseInt(a))
                            .map(([year, total]) => (
                                <Col key={year} md={6} lg={3} className="mb-3">
                                    <Card className="h-100 border-0 shadow-sm">
                                        <Card.Body>
                                            <div className="text-muted mb-1">{t("Total Sales")} {year}</div>
                                            <h3 className="mb-0">{FormatMoney(total, 2)}</h3>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        <Col md={6} lg={3} className="mb-3">
                            <Card className="h-100 border-0 shadow-sm bg-primary text-white">
                                <Card.Body>
                                    <div className="mb-1 opacity-75">{t("Grand Total")}</div>
                                    <h3 className="mb-0">{FormatMoney(grandTotal, 2)}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Chart */}
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body>
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" role="status" />
                                    <div className="mt-3">{t("Loading sales data")}...</div>
                                </div>
                            ) : error ? (
                                <div className="alert alert-danger">{t("Error")}: {error}</div>
                            ) : !chartData ? (
                                <div className="text-center py-5 text-muted">{t("No sales data available")}</div>
                            ) : (
                                <div style={{ height: 500 }}>
                                    <Bar data={chartData} options={chartOptions} />
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Detailed Data — Accordion */}
                    {!loading && sortedTableData.length > 0 && (
                        <Accordion>
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>{t("Detailed Data")}</Accordion.Header>
                                <Accordion.Body className="p-0">
                                    <div className="table-responsive">
                                        <table className="table table-striped table-hover mb-0 small">
                                            <thead>
                                                <tr>
                                                    <th className="ps-3">{t("Year")}</th>
                                                    <th>{t("Month")}</th>
                                                    <th className="text-end pe-3">{t("Amount")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedTableData.map((item, i) => (
                                                    <tr key={i}>
                                                        <td className="ps-3">{item.year}</td>
                                                        <td>
                                                            {new Date(item.year, item.month - 1)
                                                                .toLocaleString("default", { month: "long" })}
                                                        </td>
                                                        <td className="text-end pe-3">
                                                            {FormatMoney(item.amount || 0, 2)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    )}
                </>
            )}
        </Container>
    )
}

export default SalesByMonthPage
